import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useSendMessage } from '../hooks/useSendMessage';
import { useNotification } from '../hooks/useNotification';
import { EventSource, EventType, MessageType, Status, } from '../graphql/graphql';
import { deduplicateChatEventsUsingId, deduplicateSupportCaseStatusEvents, getMessageStatusEventsMap, updateChatEvents, } from '../lib/ChatEventHelpers';
import { mergeArrays, printTranscript } from '../lib/utils';
import { useReceiveEvents } from '../hooks/useReceiveEvents';
import { v4 as uuidV4 } from 'uuid';
import { log } from '../lib/logger';
import { ChatboxFrame } from '../design_system/ChatboxFrame';
import { ChatBox } from '../components/ChatBox';
import { useFullscreenImageStore } from '../stores/fullscreenImageStore';
import { ErrorBannerController } from './ErrorBannerController';
import { ErrorCodes, useErrorState } from '../stores/errorStateStore';
import { useReceiveHistory } from '../hooks/useReceiveHistory';
import { ChatBoxFooterController } from '../components/ChatBoxFooterController';
import { useShowChatboxStore } from '../stores/showChatboxStore';
import { useLineOfBusiness } from '../hooks/useLineOfBusiness';
import { useChatBoxStateStore } from '../stores/chatBoxStateStore';
import { useSendCommand } from '../hooks/useSendCommand';
import { pubsub } from './pubsub';
import { SecureFormPopupController } from './SecureFormPopupController';
import { ClientError } from 'graphql-request';
import { useSendMessageReadStatus } from '../hooks/useSendMessageReadStatus';
import { isNullable } from 'effect/Predicate';
import { ReadonlyArray } from 'effect';
import { isAdvisorTyping } from '../lib/IsAdvisorTyping';
import { useEventStore } from '../stores/eventStore';
import { CloseConfirmationDialogController } from './CloseConfirmationDialogController';
import { useCloseChat } from '../hooks/useCloseChat';
import { useConversationClosed, useConversationRestarted, } from '../stores/conversationStateStore';
import { ScrollDownNotificationController } from './ScrollDownNotificationController';
import { useIsWaitingWelcome } from '../hooks/useIsWaitingWelcome';
export const ChatController = () => {
    var _a, _b, _c;
    const { events, setEvents, updateEvents } = useEventStore();
    const { hasClosed: conversationClosed, setHasClosed: setConversationClosed } = useConversationClosed();
    const { hasRestarted: restartConversation, setHasRestarted: setRestartConversation, } = useConversationRestarted();
    const [hasSessionIdError, setHasSessionIdError] = useState(false);
    const [hasSupportCaseMismatchError, setHasSupportCaseMismatchError] = useState(false);
    const [isLastCloseEventAfterGracePeriod, setIsLastCloseEventAfterGracePeriod,] = useState(false);
    const [lastCloseEventTimestamp, setLastCloseEventTimestamp] = useState(0);
    const sendMessage = useSendMessage();
    const sendCommand = useSendCommand();
    const { lineOfBusiness } = useLineOfBusiness();
    const [showChatbox] = useShowChatboxStore(lineOfBusiness);
    const { setErrorState, setUnknownErrorThrottled } = useErrorState();
    const [isSnappedToLatest, setIsSnappedToLatest] = useState(true);
    const { setFullscreenImage } = useFullscreenImageStore();
    const [minimized, setMinimized] = useState(false);
    const { data: history, fetchPreviousPage } = useReceiveHistory();
    const sendReadMessageStatus = useSendMessageReadStatus();
    const [notifications, addNotifications] = useNotification(minimized, isSnappedToLatest);
    const { flow } = useChatBoxStateStore(lineOfBusiness);
    useEffect(() => {
        if (!conversationClosed &&
            !restartConversation &&
            events.find(e => {
                var _a, _b;
                return ((_a = e === null || e === void 0 ? void 0 : e.supportCaseStatusEvent) === null || _a === void 0 ? void 0 : _a.status) === 'CLOSED' ||
                    ((_b = e === null || e === void 0 ? void 0 : e.supportCaseStatusEvent) === null || _b === void 0 ? void 0 : _b.status) === 'POST_SURVEY';
            })) {
            setConversationClosed(true);
        }
    }, [events, conversationClosed, restartConversation, setConversationClosed]);
    useEffect(() => {
        if (restartConversation) {
            setConversationClosed(false);
        }
    }, [restartConversation, setConversationClosed]);
    useEffect(() => {
        if (!showChatbox) {
            setHasSessionIdError(false);
            setHasSupportCaseMismatchError(false);
            setLastCloseEventTimestamp(0);
            setIsLastCloseEventAfterGracePeriod(false);
        }
    }, [showChatbox]);
    const handleOnPoll = useCallback(() => {
        if (lastCloseEventTimestamp !== 0 &&
            lastCloseEventTimestamp + 10000 < Date.now()) {
            setIsLastCloseEventAfterGracePeriod(true);
        }
    }, [lastCloseEventTimestamp]);
    const handleAdvisorMessage = useCallback((data) => {
        updateEvents(prevEvents => [...prevEvents, data]);
    }, [updateEvents]);
    useEffect(() => {
        pubsub.subscribe(handleAdvisorMessage);
    }, [handleAdvisorMessage]);
    const handleReceiveEventsCompleted = useCallback((data) => {
        if (!showChatbox) {
            setEvents([]);
            return;
        }
        const allNewEvents = data.getEvents
            .filter((m) => !!m)
            .map(event => (Object.assign(Object.assign({}, event), { loading: false, failed: false, local: false, readByCustomer: false, historic: false })));
        const newEventsWithoutMessageStatusEvents = allNewEvents.filter(m => m.type !== EventType.MessageStatus);
        const deduplicatedEvents = deduplicateChatEventsUsingId(newEventsWithoutMessageStatusEvents);
        const mergedDeduplicatedEvents = deduplicateSupportCaseStatusEvents(mergeArrays(events, deduplicatedEvents));
        const newState = updateChatEvents(mergedDeduplicatedEvents, getMessageStatusEventsMap(allNewEvents));
        addNotifications(newState.filter(m => m.type !== EventType.MessagingState).length -
            events.filter(m => m.type !== EventType.MessagingState).length);
        setEvents([...newState]);
    }, [addNotifications, events, setEvents, showChatbox]);
    const isLastMessageCloseEvent = ((_a = events.at(-1)) === null || _a === void 0 ? void 0 : _a.type) === EventType.SupportCaseStatus &&
        ((_c = (_b = events.at(-1)) === null || _b === void 0 ? void 0 : _b.supportCaseStatusEvent) === null || _c === void 0 ? void 0 : _c.status) === Status.Closed;
    useEffect(() => {
        if (isLastMessageCloseEvent && lastCloseEventTimestamp === 0) {
            setLastCloseEventTimestamp(Date.now());
        }
        else {
            setLastCloseEventTimestamp(0);
        }
    }, [isLastMessageCloseEvent]);
    const handleReceiveEventsError = useCallback((err) => {
        if (!(err instanceof ClientError) || isNullable(err.response.errors)) {
            setUnknownErrorThrottled(err);
            return;
        }
        const isSessionIdError = err.response.errors.some(graphQLError => graphQLError.extensions.errorType === 'SESSION_ID_IS_NULL_OR_EMPTY');
        const isSupportCaseMismatchError = err.response.errors.some(graphQLError => graphQLError.extensions.errorType === 'SUPPORT_CASE_MISMATCH');
        if (isSessionIdError) {
            setHasSessionIdError(true);
            setErrorState({
                code: ErrorCodes.SESSION_ID_ERROR,
                originalError: err,
            });
        }
        else if (isSupportCaseMismatchError) {
            setHasSupportCaseMismatchError(true);
            setErrorState({
                code: ErrorCodes.SUPPORT_CASE_MISMATCH_ERROR,
                originalError: err,
            });
        }
        else {
            setUnknownErrorThrottled(err);
        }
    }, [setErrorState, setUnknownErrorThrottled]);
    useReceiveEvents({
        onReceiveData: handleReceiveEventsCompleted,
        onPoll: handleOnPoll,
        onError: handleReceiveEventsError,
        skip: !showChatbox ||
            isLastCloseEventAfterGracePeriod ||
            hasSessionIdError ||
            hasSupportCaseMismatchError,
    });
    const handleSendMessage = useCallback((value) => {
        const id = uuidV4();
        const timestamp = Date.now().toString();
        const encoded = value.replace(/[\u00A0-\u9999<>&]/g, i => `&#${i.charCodeAt(0)};`);
        updateEvents(prevEvents => [
            ...prevEvents,
            {
                id,
                persistedTimestamp: timestamp,
                type: EventType.Message,
                messageEvent: {
                    messageType: MessageType.Text,
                    text: { message: encoded },
                },
                source: EventSource.Customer,
                loading: true,
                failed: false,
                historic: false,
                local: true,
                readByCustomer: true,
            },
        ]);
        sendMessage({ message: value, timestamp, id }).catch(error => {
            log.error(error);
            updateEvents(prevEvents => prevEvents.map(ev => ev.id !== id ? ev : Object.assign(Object.assign({}, ev), { failed: true, loading: false })));
        });
    }, [sendMessage, updateEvents]);
    const handleCommandMessage = useCallback((command, label) => {
        const id = uuidV4();
        const timestamp = Date.now().toString();
        updateEvents(prevEvents => [
            ...prevEvents,
            {
                id,
                persistedTimestamp: timestamp,
                type: EventType.Message,
                messageEvent: {
                    messageType: MessageType.Text,
                    text: { message: label },
                },
                source: EventSource.Customer,
                loading: true,
                failed: false,
                historic: false,
                local: true,
                readByCustomer: true,
            },
        ]);
        sendCommand({ command: command, label: label, timestamp, id })
            .then(() => {
            if (command === 'restart_conversation') {
                setRestartConversation(true);
            }
        })
            .catch(error => {
            log.error(error);
            updateEvents(prevEvents => prevEvents.map(ev => ev.id !== id ? ev : Object.assign(Object.assign({}, ev), { failed: true, loading: false })));
        });
    }, [sendCommand, setRestartConversation, updateEvents]);
    const handleUpload = useCallback((payload) => {
        if (payload === null || payload === void 0 ? void 0 : payload.attachmentLink) {
            const attachment = Object.assign({ url: payload.attachmentLink }, (payload.caption && { caption: payload.caption }));
            updateEvents(prevEvents => [
                ...prevEvents,
                {
                    id: payload.id,
                    persistedTimestamp: Date.now().toString(),
                    type: EventType.Message,
                    messageEvent: {
                        messageType: MessageType.Attachment,
                        attachment,
                    },
                    source: EventSource.Customer,
                    loading: true,
                    failed: false,
                    historic: false,
                    local: true,
                    readByCustomer: true,
                },
            ]);
        }
    }, [updateEvents]);
    const { handleCloseClick } = useCloseChat();
    const messageContainerRef = useRef(null);
    const handleDownloadTranscriptClick = useCallback(() => {
        printTranscript(messageContainerRef, 'print-iframe', () => setErrorState({ code: ErrorCodes.DOWNLOAD_TRANSCRIPT_ERROR }));
    }, [setErrorState]);
    const eventsToShow = useMemo(() => {
        const deduplicatedEvents = deduplicateChatEventsUsingId(events);
        const mergedHistoryWithStateEvents = updateChatEvents(history, getMessageStatusEventsMap(history));
        const mergedHistoryDeduplicateClosedEvents = ReadonlyArray.dedupeAdjacentWith(mergedHistoryWithStateEvents, (ev1, ev2) => {
            var _a, _b;
            return (((_a = ev1.supportCaseStatusEvent) === null || _a === void 0 ? void 0 : _a.status) === Status.Closed &&
                ((_b = ev2.supportCaseStatusEvent) === null || _b === void 0 ? void 0 : _b.status) === Status.Closed);
        });
        const historicalData = mergedHistoryDeduplicateClosedEvents.map(ev => (Object.assign(Object.assign({}, ev), { historic: true })));
        const newDeduplicatedEventsWithHistorySet = deduplicatedEvents.map(d => (Object.assign(Object.assign({}, d), { historic: false })));
        return mergeArrays(historicalData, newDeduplicatedEventsWithHistorySet).filter(e => e.type !== EventType.MessagingState);
    }, [events, history]);
    const isWaitingWelcome = useIsWaitingWelcome();
    const advisorTypingEvent = isAdvisorTyping(events);
    if (!showChatbox)
        return null;
    return (React.createElement(ChatboxFrame, { notificationCount: notifications, onCloseClick: handleCloseClick, onMinimizeChange: isMinimized => setMinimized(isMinimized) },
        React.createElement(ErrorBannerController, null),
        React.createElement(ChatBox, { ref: messageContainerRef, events: eventsToShow, currentCaseEventsCount: events.length, flow: flow, advisorTypingEvent: advisorTypingEvent, showWelcome: isWaitingWelcome, onOptionClick: option => handleSendMessage(option), onSnapToLatestChange: setIsSnappedToLatest, onAttachmentClick: setFullscreenImage, onMessageRowInChatViewport: sendReadMessageStatus, onCloseToTop: fetchPreviousPage, onCommandClick: handleCommandMessage, isLastMessageCloseEvent: isLastMessageCloseEvent }),
        React.createElement(ChatBoxFooterController, { isLastMessageCloseEvent: isLastMessageCloseEvent, isWaitingWelcome: isWaitingWelcome, onSubmit: handleSendMessage, onUpload: handleUpload, onDownloadTranscriptClick: handleDownloadTranscriptClick }),
        React.createElement(ScrollDownNotificationController, { notifications: notifications, messageContainerRef: messageContainerRef }),
        React.createElement(CloseConfirmationDialogController, null),
        React.createElement(SecureFormPopupController, null)));
};
